/**
 * Platform-auth-service API — typed client for social OAuth connections.
 *
 * Gateway prefix /api/v1/platforms (see api-gateway get_service_routes and
 * platform-auth-service main.py, which mounts the router at /api/v1/platforms).
 * Mirrors services-python/platform-auth-service/src/domain/models.py.
 *
 * NOTE: platform-auth-service implements a generic OAuth flow for all 7
 * providers in OAUTH_PLATFORMS below (linkedin/twitter/instagram/facebook/
 * youtube/pinterest/tiktok) plus meta_ads/google_ads for ad-account links.
 * Each provider is purely credential-gated: `GET /providers` returns
 * `configured: false` until the owner sets its <PROVIDER>_CLIENT_ID/SECRET.
 * Only LinkedIn is credentialed in prod today; the rest degrade honestly to a
 * disabled "connect" affordance. (Telegram uses a bot token via the publishing
 * accounts API, not this OAuth service.)
 */

import { api } from '@/lib/api/client';

const BASE = '/api/v1/platforms';

// ---------------------------------------------------------------------------
// Enums (const arrays so the UI can iterate / narrow)
// ---------------------------------------------------------------------------

/** Platforms with an OAuth flow in platform-auth-service. */
export const OAUTH_PLATFORMS = [
  'linkedin',
  'twitter',
  'instagram',
  'facebook',
  'youtube',
  'pinterest',
  'tiktok',
] as const;
export type OAuthPlatform = (typeof OAUTH_PLATFORMS)[number];

/**
 * `needs_reconnect` mirrors platform-auth-service's ConnectionStatus.NEEDS_RECONNECT
 * (src/domain/models.py): the stored OAuth token could not be DECRYPTED
 * server-side (corrupted ciphertext, an unknown/rotated key_version, or a
 * legacy plaintext value) — a server-side integrity problem distinct from
 * `expired` (a normal provider-side token lifetime). A connection in this
 * state is silently unpublishable and must never be presented as healthy —
 * see `isHealthyConnectionStatus` below.
 */
export const CONNECTION_STATUSES = ['active', 'expired', 'revoked', 'needs_reconnect'] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

/** Only `active` reflects a connection that can actually publish right now. */
export function isHealthyConnectionStatus(status: ConnectionStatus): boolean {
  return status === 'active';
}

export function isOAuthPlatform(platform: string): platform is OAuthPlatform {
  return (OAUTH_PLATFORMS as readonly string[]).includes(platform);
}

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

/** Mirrors PlatformConnectionResponse (tokens never leave the backend). */
export interface PlatformConnection {
  id: string;
  platform: OAuthPlatform;
  platform_user_id: string;
  platform_username: string;
  status: ConnectionStatus;
  /** Lead-work kind: personal | meta_business | facebook_pages | youtube_channel. */
  connection_kind: string;
  /** Non-secret capability metadata (pages, ig_user_id, channels, granted_scopes). */
  metadata: Record<string, unknown>;
  /** Concrete lead capabilities this connection unlocks. */
  capabilities: string[];
  token_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuthUrlResponse {
  auth_url: string;
  state: string;
}

/**
 * Mirrors platform-auth-service ProviderInfo — connectable providers + honest
 * lead capabilities / ToS limits (LinkedIn has no people-search/auto-connect,
 * Meta forbids cold-DM). Drives the "Comptes connectés" UI.
 */
export interface ProviderInfo {
  platform: OAuthPlatform;
  label: string;
  configured: boolean;
  connected: boolean;
  capabilities: string[];
  lead_scopes: string[];
  supports_people_search: boolean;
  notes: string;
}

/** GET /providers — connectable providers with capabilities + ToS notes. */
export function listProviders(): Promise<ProviderInfo[]> {
  return api.get<ProviderInfo[]>(`${BASE}/providers`);
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** GET /connections — the caller's OAuth connections (no tokens). */
export function listConnections(): Promise<PlatformConnection[]> {
  return api.get<PlatformConnection[]>(`${BASE}/connections`);
}

/**
 * GET /auth-url?platform=… — authorization URL to redirect the browser to.
 * The provider callback returns to /platforms?status=success&platform=… .
 */
export function getAuthUrl(platform: OAuthPlatform): Promise<AuthUrlResponse> {
  return api.get<AuthUrlResponse>(`${BASE}/auth-url?platform=${encodeURIComponent(platform)}`);
}

/** DELETE /connections/{id} — disconnect a platform. */
export function deleteConnection(id: string): Promise<void> {
  return api.delete<void>(`${BASE}/connections/${encodeURIComponent(id)}`);
}

/** POST /connections/{id}/refresh — refresh the stored tokens. */
export function refreshConnection(id: string): Promise<PlatformConnection> {
  return api.post<PlatformConnection>(`${BASE}/connections/${encodeURIComponent(id)}/refresh`);
}

// ---------------------------------------------------------------------------
// Publishing accounts (publishing-service, /api/v1/publishing prefix)
//
// Connecting an identity (LinkedIn/Facebook) makes the backend create one
// publishable "account" per publishable surface: the personal profile PLUS
// every Company Page / Facebook Page the user administers. The Platforms page
// surfaces these so a freshly-connected identity and the Pages it manages are
// visible and can be enabled/disabled as publish targets.
//
// This is a DIFFERENT service from platform-auth (whose connections live above),
// but it is exposed here because it is the natural place to manage what a
// connection unlocks. We keep a MINIMAL local mirror of the publishing-service
// account response — only the fields this page needs — because this file's edit
// perimeter cannot reach the richer publishing client, and that client's
// `SocialAccount` type predates account_type / parent_account_id / avatar_url.
// ---------------------------------------------------------------------------

const PUBLISHING_BASE = '/api/v1/publishing';

/** How a publishable account relates to the connected identity. */
export const PUBLISHING_ACCOUNT_TYPES = ['personal', 'organization', 'page'] as const;
export type PublishingAccountType = (typeof PUBLISHING_ACCOUNT_TYPES)[number];

/**
 * A publishable account (publishing-service SocialAccountResponse, trimmed).
 * `account_type` is typed as a union for the known values but read defensively
 * in the UI — the backend may return other/legacy values (e.g. bot accounts).
 */
export interface PublishingAccount {
  id: string;
  platform: string;
  account_name: string;
  account_id: string;
  /** personal profile vs an organization / page the user administers. */
  account_type: PublishingAccountType | string;
  /** Pages point at the profile they belong to (its row id or platform id). */
  parent_account_id: string | null;
  avatar_url: string | null;
  status: string;
  created_at?: string;
  updated_at?: string;
}

/** Envelope shape the publishing accounts list returns (mirrors Paginated<T>). */
interface PublishingAccountPage {
  items: PublishingAccount[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export type ListPublishingAccountsParams = {
  tenant_id?: string;
  user_id?: string;
  platform?: string;
  limit?: number;
};

/**
 * GET /api/v1/publishing/accounts — the tenant's publishable identities and the
 * Pages they administer. Returns the unwrapped item list (high default limit so
 * every profile + page is surfaced without paging on this management view).
 */
export async function listPublishingAccounts(
  params: ListPublishingAccountsParams = {},
): Promise<PublishingAccount[]> {
  const search = new URLSearchParams();
  search.set('limit', String(params.limit ?? 200));
  if (params.tenant_id) search.set('tenant_id', params.tenant_id);
  if (params.user_id) search.set('user_id', params.user_id);
  if (params.platform) search.set('platform', params.platform);
  const page = await api.get<PublishingAccountPage>(
    `${PUBLISHING_BASE}/accounts?${search.toString()}`,
  );
  return page.items;
}

/**
 * DELETE /api/v1/publishing/accounts/{id} — remove a publish target (disable a
 * profile or Page as a publishing destination). Re-enabling is done by
 * reconnecting the platform, which recreates the accounts.
 */
export function deletePublishingAccount(id: string): Promise<void> {
  return api.delete<void>(`${PUBLISHING_BASE}/accounts/${encodeURIComponent(id)}`);
}

// ---------------------------------------------------------------------------
// Page re-sync (platform-auth-service, /api/v1/platforms prefix)
//
// A connected identity administers Pages (LinkedIn Company Pages / Facebook
// Pages / YouTube channels) that may change over time. `resyncPages` re-reads
// the provider, refreshes the stored page/channel set and PUSHES each newly
// discovered surface into the publishing service as a publishable account, so
// they appear in the personal→pages tree without reconnecting the whole
// platform. `getPages` returns a non-authoritative snapshot of what the auth
// service currently knows (the publishing accounts list stays the source of
// truth for publish targets).
// ---------------------------------------------------------------------------

/** Per-platform breakdown of how many pages were pushed by a resync. */
export interface ResyncPagesGroup {
  platform: string;
  count: number;
}

/** POST /{platform}/resync-pages result — total pushed + per-group counts. */
export interface ResyncPagesResult {
  platform: string;
  pushed: number;
  groups: ResyncPagesGroup[];
}

/**
 * POST /api/v1/platforms/{platform}/resync-pages — re-discover the Pages /
 * channels the connected identity administers and push them to the publishing
 * service as publishable accounts.
 *
 * Throws an ApiError with status 404 when the platform is not connected, or 409
 * when the stored token is expired and cannot be refreshed (the caller must ask
 * the user to reconnect).
 */
export function resyncPages(platform: string): Promise<ResyncPagesResult> {
  return api.post<ResyncPagesResult>(`${BASE}/${encodeURIComponent(platform)}/resync-pages`);
}

/**
 * A non-authoritative snapshot of the Pages / channels the auth service knows
 * for a connection. `pages` / `channels` are loosely typed — the shape is
 * provider-specific and this snapshot is advisory only (publishing GET
 * /accounts is the source of truth for actual publish targets).
 */
export interface PlatformPagesSnapshot {
  platform: string;
  connection_kind: string;
  pages: Array<Record<string, unknown>>;
  channels: Array<Record<string, unknown>>;
  source: string;
}

/**
 * GET /api/v1/platforms/{platform}/pages — the current (non-authoritative) page
 * / channel snapshot for a connected platform.
 */
export function getPages(platform: string): Promise<PlatformPagesSnapshot> {
  return api.get<PlatformPagesSnapshot>(`${BASE}/${encodeURIComponent(platform)}/pages`);
}
